import { useState } from 'react';
import { Button } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useIsViewer } from '../auth/hooks/useIsViewer';

import { CreateUpdateSnapshotHistorySetting } from './types';
import useInLightMode from '../../hooks/useInLightMode';
import { grey } from '@mui/material/colors';
import { useIsSnapshotLocked } from '../analyzers/hooks/useIsSnapshotLocked';
import { useIsSnapshotRecording } from './hooks/useIsSnapshotRecording';

import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useSnapshotHistorySettings } from './api/getSnapshotHistorySettings';
import { useCreateSnapshotHistorySettings } from './api/createSnapshotHistorySettings';
import { useUpdateSnapshotHistorySettings } from './api/updateSnapshotHistorySettings';
import { LockOutlined } from '@mui/icons-material';
import RecordDurationDialog from './RecordDurationDialog';
import { add } from 'date-fns';

interface Props {
  analyzerId: number;
  snapshotId: number;
}

const RecordButton = ({ analyzerId, snapshotId }: Props) => {
  const inLightMode = useInLightMode();
  const [durationDialogOpen, setDurationDialogOpen] = useState<boolean>(false);

  const { data: analyzer } = useAnalyzer({ analyzerId });
  const isViewer = useIsViewer();

  const { data: snapshot } = useSnapshot({ snapshotId });
  const { data: historySettings } = useSnapshotHistorySettings({ snapshotId });
  const createSnapshotHistorySettingsMutation =
    useCreateSnapshotHistorySettings({});
  const updateSnapshotHistorySettingsMutation =
    useUpdateSnapshotHistorySettings({
      snapshotId,
    });

  const isSnapshotLocked =
    useIsSnapshotLocked(snapshot) || !!snapshot?.recording_locked;

  const isRecording = useIsSnapshotRecording(analyzer?.mode, historySettings);

  const recordColor: string = isRecording
    ? 'red'
    : inLightMode
    ? grey[300]
    : 'white';

  const handleRecordClicked = async () => {
    if (!historySettings) {
      const newHistorySettings: CreateUpdateSnapshotHistorySetting = {
        snapshot_id: snapshotId,
      };

      if (analyzer?.mode === 0) {
        setDurationDialogOpen(true);
        return;
      } else if (analyzer?.mode === 1) {
        newHistorySettings['polling_continuous_mode'] = true;
      }

      await createSnapshotHistorySettingsMutation.mutateAsync({
        data: newHistorySettings,
        snapshotId,
      });
    } else {
      const updatedHistorySettings = {
        ...historySettings,
      };

      if (analyzer?.mode === 0) {
        if (!isRecording) {
          setDurationDialogOpen(true);
          return;
        } else {
          updatedHistorySettings['continuous_mode'] =
            !historySettings.continuous_mode;
        }
      } else if (analyzer?.mode === 1) {
        updatedHistorySettings['polling_continuous_mode'] =
          !historySettings.polling_continuous_mode;
      }

      await updateSnapshotHistorySettingsMutation.mutateAsync({
        data: updatedHistorySettings,
        snapshotId,
      });
    }
  };

  const handleContinuousRecordingStarted = async (
    recordDurationValue: number,
    recordDurationUnit: string
  ) => {
    const currentDate = new Date();
    const duration: Duration = {
      [recordDurationUnit]: recordDurationValue,
    };
    const recordEndTime = add(currentDate, duration);
    const updatedHistorySettings = {
      ...historySettings,
      snapshot_id: snapshotId,
      continuous_mode_end_time: recordEndTime.toISOString(),
      continuous_mode: true,
    };

    if (!historySettings) {
      await createSnapshotHistorySettingsMutation.mutateAsync({
        data: updatedHistorySettings,
        snapshotId,
      });
    } else {
      await updateSnapshotHistorySettingsMutation.mutateAsync({
        data: updatedHistorySettings,
        snapshotId,
      });
    }
  };

  const startIcon = snapshot?.recording_locked ? (
    <LockOutlined />
  ) : (
    <FiberManualRecordIcon style={{ fill: recordColor }} />
  );

  return (
    <>
      <Button
        variant='outlined'
        startIcon={startIcon}
        onClick={handleRecordClicked}
        disabled={isViewer || isSnapshotLocked}
        fullWidth
      >
        {isRecording ? 'Stop Recording' : 'Record'}
      </Button>
      <RecordDurationDialog
        open={durationDialogOpen}
        handleStartRecording={handleContinuousRecordingStarted}
        handleClose={() => setDurationDialogOpen(false)}
      />
    </>
  );
};

export default RecordButton;
